import { useState, useCallback, useEffect } from "react";
import { useSocket } from "../services/GlobalSocket";
import myAxios from "../Component/AxiosUrl";

// Default dashboard data structure
const getDefaultDashboardData = () => ({
  overview: {
    totalRevenue: {
      value: 0,
      formatted: "$0",
      change: 0,
      period: "This month",
    },
    orders: { value: 0, formatted: "0", change: 0, period: "Last 30 days" },
    activeUsers: {
      value: 0,
      formatted: "0",
      change: 0,
      period: "Online now",
    },
  },
  revenueTrend: [
    { month: "Jan", revenue: 0 },
    { month: "Feb", revenue: 0 },
    { month: "Mar", revenue: 0 },
    { month: "Apr", revenue: 0 },
    { month: "May", revenue: 0 },
    { month: "Jun", revenue: 0 },
  ],
  trafficSources: [
    { source: "CPU Usage", value: 0, color: "#10b981" },
    { source: "Memory Usage", value: 0, color: "#10b981" },
    { source: "Load Average", value: 0, color: "#06b6d4" },
    { source: "Users Online", value: 0, color: "#8b5cf6" },
  ],
  recentActivity: [
    {
      id: 1,
      action: "New user registered",
      time: "2 minutes ago",
      type: "user",
      details: "john.doe@example.com",
    },
    {
      id: 2,
      action: "Payment completed",
      time: "5 minutes ago",
      type: "payment",
      details: "$49.99",
    },
    {
      id: 3,
      action: "New order received",
      time: "12 minutes ago",
      type: "order",
      details: "Order #12345",
    },
    {
      id: 4,
      action: "User upgraded plan",
      time: "25 minutes ago",
      type: "upgrade",
      details: "Premium Plan",
    },
    {
      id: 5,
      action: "Support ticket created",
      time: "1 hour ago",
      type: "support",
      details: "Ticket #4567",
    },
  ],
  quickStats: {
    avgOrderValue: { value: 0, formatted: "$0.00" },
    conversionRate: { value: 0, formatted: "0%" },
    bounceRate: { value: 0, formatted: "0%" },
    activeSessions: { value: 0, formatted: "0" },
  },
  serverStatus: {
    onlineUsers: {
      count: 0,
      percentage: 0,
      users: [],
    },
    summary: {
      totalUsers: 0,
      onlineUsers: 0,
      cpuUsage: 0,
      memoryUsage: 0,
      loadAverage: 0,
      uptime: "0s",
    },
  },
});

// Fallback data for error states
const getFallbackDashboardData = () => ({
  overview: {
    totalRevenue: {
      value: 32845000,
      formatted: "$328,450",
      change: 12.5,
      period: "This month",
    },
    orders: {
      value: 1245,
      formatted: "1,245",
      change: 8.2,
      period: "Last 30 days",
    },
    activeUsers: {
      value: 8942,
      formatted: "8,942",
      change: 2.1,
      period: "Online now",
    },
  },
  revenueTrend: [
    { month: "Jan", revenue: 45000 },
    { month: "Feb", revenue: 52000 },
    { month: "Mar", revenue: 48000 },
    { month: "Apr", revenue: 61000 },
    { month: "May", revenue: 55000 },
    { month: "Jun", revenue: 67000 },
  ],
  trafficSources: [
    { source: "Direct", value: 45, color: "#6366f1" },
    { source: "Social", value: 25, color: "#8b5cf6" },
    { source: "Search", value: 20, color: "#06b6d4" },
    { source: "Email", value: 10, color: "#10b981" },
  ],
  recentActivity: [
    {
      id: 1,
      action: "New order received",
      time: "2 minutes ago",
      type: "order",
      details: "Order #12345",
    },
    {
      id: 2,
      action: "Payment processed",
      time: "15 minutes ago",
      type: "payment",
      details: "$89.99",
    },
    {
      id: 3,
      action: "New user registered",
      time: "1 hour ago",
      type: "user",
      details: "jane.smith@example.com",
    },
    {
      id: 4,
      action: "League created",
      time: "2 hours ago",
      type: "league",
      details: "Premier League Fantasy",
    },
    {
      id: 5,
      action: "Player transferred",
      time: "3 hours ago",
      type: "transfer",
      details: "Messi to Barcelona",
    },
  ],
  quickStats: {
    avgOrderValue: { value: 24550, formatted: "$245.50" },
    conversionRate: { value: 3.2, formatted: "3.2%" },
    bounceRate: { value: 42.8, formatted: "42.8%" },
    activeSessions: { value: 1234, formatted: "1,234" },
  },
  serverStatus: {
    onlineUsers: {
      count: 123,
      percentage: 85,
      users: [
        { id: 1, name: "User A", status: "Online" },
        { id: 2, name: "User B", status: "Offline" },
        { id: 3, name: "User C", status: "Online" },
        { id: 4, name: "User D", status: "Online" },
        { id: 5, name: "User E", status: "Offline" },
      ],
    },
    summary: {
      totalUsers: 145,
      onlineUsers: 123,
      cpuUsage: 75,
      memoryUsage: 60,
      loadAverage: 1.2,
      uptime: "12h 30m",
    },
  },
});

export const useDashboardData = () => {
  const [dashboardData, setDashboardData] = useState(getDefaultDashboardData());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const socket = useSocket();

  // Fetch dashboard data from API
  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);

      const response = await myAxios.get("/dashboard/complete");

      if (response.data.success) {
        const data = response.data.data;

        // Ensure revenue trend has valid data
        const revenueTrend =
          data.revenueTrend && data.revenueTrend.length > 0
            ? data.revenueTrend.map((item) => ({
                month: item.month,
                revenue: item.revenue || 0,
              }))
            : [
                { month: "Jan", revenue: 45000 },
                { month: "Feb", revenue: 52000 },
                { month: "Mar", revenue: 48000 },
                { month: "Apr", revenue: 61000 },
                { month: "May", revenue: 55000 },
                { month: "Jun", revenue: 67000 },
              ];

        setDashboardData({
          ...data,
          revenueTrend,
        });
        setLastUpdate(new Date());
      } else {
        throw new Error(
          response.data.message || "Failed to fetch dashboard data"
        );
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(error.message);
      setDashboardData(getFallbackDashboardData());
    } finally {
      setLoading(false);
    }
  }, []);

  // Setup real-time listeners for socket events
  const setupRealtimeListeners = useCallback(() => {
    // Throttle functions to prevent excessive updates
    let lastDashboardUpdate = 0;
    let lastServerStatusUpdate = 0;
    let lastOnlineUsersUpdate = 0;
    const throttleDelay = 2000; // 2 seconds throttle

    // Listen for dashboard updates
    const handleDashboardUpdate = (event) => {
      const now = Date.now();
      if (now - lastDashboardUpdate < throttleDelay) {
        return; // Skip if too soon
      }
      lastDashboardUpdate = now;

      const data = event.detail;
      setDashboardData((prevData) => ({
        ...prevData,
        ...data,
      }));
      setLastUpdate(new Date());
    };

    // Listen for server status updates
    const handleServerStatusUpdate = (event) => {
      const now = Date.now();
      if (now - lastServerStatusUpdate < throttleDelay) {
        return; // Skip if too soon
      }
      lastServerStatusUpdate = now;

      const data = event.detail;
      setDashboardData((prevData) => ({
        ...prevData,
        trafficSources: data.serverStatusChart || prevData.trafficSources,
        serverStatus: {
          ...prevData.serverStatus,
          ...data,
        },
      }));
      setLastUpdate(new Date());
    };

    // Listen for online users updates
    const handleOnlineUsersUpdate = (event) => {
      const now = Date.now();
      if (now - lastOnlineUsersUpdate < throttleDelay) {
        return; // Skip if too soon
      }
      lastOnlineUsersUpdate = now;

      const data = event.detail;
      setDashboardData((prevData) => ({
        ...prevData,
        serverStatus: {
          ...prevData.serverStatus,
          onlineUsers: data.onlineUsers || prevData.serverStatus.onlineUsers,
        },
      }));
      setLastUpdate(new Date());
    };

    // Add event listeners
    window.addEventListener("adminDashboardUpdate", handleDashboardUpdate);
    window.addEventListener("serverStatusUpdate", handleServerStatusUpdate);
    window.addEventListener("onlineUsersUpdate", handleOnlineUsersUpdate);

    // Return cleanup function
    return () => {
      window.removeEventListener("adminDashboardUpdate", handleDashboardUpdate);
      window.removeEventListener(
        "serverStatusUpdate",
        handleServerStatusUpdate
      );
      window.removeEventListener("onlineUsersUpdate", handleOnlineUsersUpdate);
    };
  }, []);

  // Initialize dashboard data and listeners
  useEffect(() => {
    fetchDashboardData();
    const cleanup = setupRealtimeListeners();
    return cleanup;
  }, [fetchDashboardData, setupRealtimeListeners]);

  // Enhanced real-time data requests
  useEffect(() => {
    if (socket.isConnected) {
      // Request initial data
      socket.requestServerStatus();
      socket.requestDashboardData();

      // Set up periodic requests for real-time data
      const interval = setInterval(() => {
        if (socket.isConnected) {
          socket.requestServerStatus();
        }
      }, 15000); // Request every 15 seconds

      return () => clearInterval(interval);
    }
  }, [socket.isConnected, socket]);

  return {
    dashboardData,
    loading,
    error,
    lastUpdate,
    fetchDashboardData,
  };
};
